import { create } from 'zustand';
import type { BeamAnalyzeRequest, BeamType, PointLoad } from './types';

interface BeamState {
  request: BeamAnalyzeRequest;
  /** One independent request per beam type — switching the type loads the
   *  type's saved values (or its defaults the first time). */
  requests: Record<BeamType, BeamAnalyzeRequest>;
  /** Active beam type's applied loads (mirror of appliedLoadsByType[currentType]). */
  appliedLoads: PointLoad[];
  /** One independent set of point loads PER beam type — adding a load on
   *  "Rectangular" must NOT appear on "Peraltada", etc. */
  appliedLoadsByType: Record<BeamType, PointLoad[]>;
  setType: (t: BeamType) => void;
  setSection: (patch: Partial<BeamAnalyzeRequest['section']>) => void;
  setMaterials: (patch: Partial<BeamAnalyzeRequest['materials']>) => void;
  setLoads: (patch: Partial<BeamAnalyzeRequest['loads']>) => void;
  setReinforcement: (patch: Partial<BeamAnalyzeRequest['reinforcement']>) => void;
  addPointLoad: (load: PointLoad) => void;
  updatePointLoad: (id: string, patch: Partial<PointLoad>) => void;
  removePointLoad: (id: string) => void;
  /** Replace the active request — used when loading a saved design. */
  loadRequest: (req: BeamAnalyzeRequest) => void;
}

/** Sensible defaults per beam type — each type starts from a typical preset
 *  so the user can switch types without re-entering everything. */
function defaultsFor(type: BeamType): BeamAnalyzeRequest {
  const baseMat = { fc: 280, fy: 4200, cover: 4, TMA: 1.905 };
  const baseRebar = { stirrup_diam: '3/8', stirrup_legs: 2 } as BeamAnalyzeRequest['reinforcement'];

  switch (type) {
    case 'rectangular':
      return {
        section: { type, b: 25, h: 50, L_span: 500 },
        materials: baseMat,
        loads: {
          Mu_neg_left: 1_800_000, Mu_pos_mid: 1_200_000, Mu_neg_right: 1_800_000,
          Vu_left: 18_000, Vu_right: 18_000,
        },
        reinforcement: baseRebar,
      };
    case 'peraltada':
      // Deep beam: h ≥ L/4. Typical: short span, very tall beam.
      return {
        section: { type, b: 30, h: 100, L_span: 350 },
        materials: baseMat,
        loads: {
          Mu_neg_left: 4_500_000, Mu_pos_mid: 3_000_000, Mu_neg_right: 4_500_000,
          Vu_left: 35_000, Vu_right: 35_000,
        },
        reinforcement: baseRebar,
      };
    case 'chata':
      // Flat beam: b > h. Cargas moderadas — sección ancha y baja resiste
      // bastante; no debería gatillar doblemente con valores típicos.
      return {
        section: { type, b: 40, h: 25, L_span: 450 },
        materials: baseMat,
        loads: {
          Mu_neg_left: 600_000, Mu_pos_mid: 450_000, Mu_neg_right: 600_000,
          Vu_left: 8_000, Vu_right: 8_000,
        },
        reinforcement: baseRebar,
      };
    case 'solera':
      // Solera de albañilería (E.070). Pequeña, no sísmica. Práctica habitual:
      // 4Ø3/8" + estribo Ø1/4 @ 20 (forzamos diam=3/8 y cargas mínimas).
      return {
        section: { type, b: 13, h: 20, L_span: 350 },
        materials: { ...baseMat, fc: 175 },
        loads: {
          Mu_neg_left: 50_000, Mu_pos_mid: 50_000, Mu_neg_right: 50_000,
          Vu_left: 1000, Vu_right: 1000,
        },
        reinforcement: { ...baseRebar, diam_pos: '3/8', diam_neg: '3/8', n_pos: 2, n_neg: 2 },
      };
    case 'confinamiento':
      // Confinamiento de albañilería (E.070). 4Ø3/8" típico + estribo Ø1/4@20.
      return {
        section: { type, b: 15, h: 20, L_span: 350 },
        materials: { ...baseMat, fc: 210 },
        loads: {
          Mu_neg_left: 80_000, Mu_pos_mid: 60_000, Mu_neg_right: 80_000,
          Vu_left: 1500, Vu_right: 1500,
        },
        reinforcement: { ...baseRebar, diam_pos: '3/8', diam_neg: '3/8', n_pos: 2, n_neg: 2 },
      };
    case 'invertida':
      // Viga invertida: misma geometría que rectangular pero el armado se ve
      // espejado (top = tracción positiva, bot = tracción negativa).
      return {
        section: { type, b: 25, h: 50, L_span: 500 },
        materials: baseMat,
        loads: {
          Mu_neg_left: 1_800_000, Mu_pos_mid: 1_200_000, Mu_neg_right: 1_800_000,
          Vu_left: 18_000, Vu_right: 18_000,
        },
        reinforcement: baseRebar,
      };
    case 'arriostre':
      // Viga de arriostre / viga de atado: liviana, mínima armadura, casi siempre
      // gobernada por As_min (no por momento de demanda).
      return {
        section: { type, b: 20, h: 25, L_span: 350 },
        materials: baseMat,
        loads: {
          Mu_neg_left: 100_000, Mu_pos_mid: 60_000, Mu_neg_right: 100_000,
          Vu_left: 2000, Vu_right: 2000,
        },
        reinforcement: { ...baseRebar, diam_pos: '1/2', diam_neg: '1/2', n_pos: 2, n_neg: 2 },
      };
    case 'borde':
      // Viga de borde: típicamente recibe carga asimétrica. Sección 20×35,
      // armado mínimo 2Ø1/2" + 2Ø1/2".
      return {
        section: { type, b: 20, h: 35, L_span: 450 },
        materials: baseMat,
        loads: {
          Mu_neg_left: 500_000, Mu_pos_mid: 350_000, Mu_neg_right: 500_000,
          Vu_left: 6000, Vu_right: 6000,
        },
        reinforcement: { ...baseRebar, diam_pos: '1/2', diam_neg: '1/2', n_pos: 2, n_neg: 2 },
      };
  }
}

const initialRequests: Record<BeamType, BeamAnalyzeRequest> = {
  rectangular: defaultsFor('rectangular'),
  peraltada: defaultsFor('peraltada'),
  chata: defaultsFor('chata'),
  solera: defaultsFor('solera'),
  confinamiento: defaultsFor('confinamiento'),
  invertida: defaultsFor('invertida'),
  arriostre: defaultsFor('arriostre'),
  borde: defaultsFor('borde'),
};

const emptyLoadsByType: Record<BeamType, PointLoad[]> = {
  rectangular: [],
  peraltada: [],
  chata: [],
  solera: [],
  confinamiento: [],
  invertida: [],
  arriostre: [],
  borde: [],
};

export const useBeamStore = create<BeamState>((set) => ({
  request: initialRequests.rectangular,
  requests: initialRequests,
  appliedLoads: [],
  appliedLoadsByType: emptyLoadsByType,
  addPointLoad: (load) =>
    set((s) => {
      const t = s.request.section.type;
      const next = [...s.appliedLoads, load];
      return {
        appliedLoads: next,
        appliedLoadsByType: { ...s.appliedLoadsByType, [t]: next },
      };
    }),
  updatePointLoad: (id, patch) =>
    set((s) => {
      const t = s.request.section.type;
      const next = s.appliedLoads.map((l) => (l.id === id ? { ...l, ...patch } : l));
      return {
        appliedLoads: next,
        appliedLoadsByType: { ...s.appliedLoadsByType, [t]: next },
      };
    }),
  removePointLoad: (id) =>
    set((s) => {
      const t = s.request.section.type;
      const next = s.appliedLoads.filter((l) => l.id !== id);
      return {
        appliedLoads: next,
        appliedLoadsByType: { ...s.appliedLoadsByType, [t]: next },
      };
    }),
  setType: (type) =>
    set((s) => {
      const prevType = s.request.section.type;
      // Save current request AND current loads under the previous type's slot.
      const savedRequests = { ...s.requests, [prevType]: s.request };
      const savedLoadsByType = { ...s.appliedLoadsByType, [prevType]: s.appliedLoads };
      const nextRequest = savedRequests[type] ?? defaultsFor(type);
      // Defensive: enforce that the slot has matching section.type
      const fixed: BeamAnalyzeRequest = {
        ...nextRequest,
        section: { ...nextRequest.section, type },
      };
      return {
        requests: savedRequests,
        request: fixed,
        appliedLoadsByType: savedLoadsByType,
        appliedLoads: savedLoadsByType[type] ?? [],
      };
    }),
  setSection: (patch) =>
    set((s) => ({ request: { ...s.request, section: { ...s.request.section, ...patch } } })),
  setMaterials: (patch) =>
    set((s) => ({ request: { ...s.request, materials: { ...s.request.materials, ...patch } } })),
  setLoads: (patch) =>
    set((s) => ({ request: { ...s.request, loads: { ...s.request.loads, ...patch } } })),
  setReinforcement: (patch) =>
    set((s) => ({
      request: { ...s.request, reinforcement: { ...s.request.reinforcement, ...patch } },
    })),
  loadRequest: (req) =>
    set((s) => ({
      request: req,
      requests: { ...s.requests, [req.section.type]: req },
    })),
}));
